import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Api from 'api';
import type { RateBenchmarkResponse } from 'api/Api';
import MaterialSelector from 'components/Selector/MaterialSelector';
import DefaultButton from 'components/Buttons/DefaultButton';
import { rateOptions } from '../PostJobPage/PostJob.utils';
import {
  buildRateBenchmarkRequestParamsFromJob,
  getClampedRecommendedRate,
  getRateGuidanceCopy,
  getRateGuidanceState,
} from 'utils/rateGuidance';
import postJobStyles from '../PostJobPage/PostJob.module.scss';

export interface EditProposedRateModalProps {
  job: {
    id: number;
    type: number;
    proposedRate?: number;
    locationId?: number;
    location?: any;
    startDate?: string;
    shifts?: Array<{
      shiftDate?: string;
      postedStart?: string;
      postedEnd?: string;
    }>;
  };
  onClose: () => void;
  onSaved: (proposedRate: number) => void;
}

const EditProposedRateModal: React.FC<EditProposedRateModalProps> = ({
  job,
  onClose,
  onSaved,
}) => {
  const initialRate = Number(job.proposedRate) || 0;
  const [selectedRate, setSelectedRate] = useState<number>(initialRate);
  const [rateBenchmark, setRateBenchmark] = useState<RateBenchmarkResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const hasComparableRole = job.type === 0 || job.type === 1;

  useEffect(() => {
    let cancelled = false;
    const params = buildRateBenchmarkRequestParamsFromJob(job);
    if (!params || !hasComparableRole) {
      setRateBenchmark(null);
      return undefined;
    }
    Api.getRateBenchmarks(params)
      .then((data) => {
        if (!cancelled) setRateBenchmark(data);
      })
      .catch(() => {
        if (!cancelled) setRateBenchmark(null);
      });
    return () => {
      cancelled = true;
    };
  }, [job, hasComparableRole]);

  const rateGuidanceState = useMemo(
    () => (hasComparableRole ? getRateGuidanceState(Number(selectedRate), rateBenchmark) : null),
    [hasComparableRole, selectedRate, rateBenchmark],
  );

  const rateGuidanceCopy = useMemo(
    () => (hasComparableRole ? getRateGuidanceCopy(rateGuidanceState, rateBenchmark) : null),
    [hasComparableRole, rateBenchmark, rateGuidanceState],
  );

  const recommendedRate = useMemo(
    () => (hasComparableRole
      ? getClampedRecommendedRate(rateBenchmark, Number(job.type) as 0 | 1)
      : undefined),
    [hasComparableRole, rateBenchmark, job.type],
  );

  const isLowering = Number(selectedRate) < Number(initialRate);
  const showGuidance = Boolean(
    isLowering
    && rateGuidanceCopy
    && rateBenchmark
    && rateBenchmark.sampleSize > 0,
  );

  const handleSave = useCallback(async () => {
    if (!selectedRate || selectedRate === initialRate) {
      onClose();
      return;
    }
    setSaving(true);
    setError('');
    try {
      await Api.updateToken();
      const result = await Api.updateProposedRate({
        jobId: job.id,
        proposedRate: selectedRate,
      });
      onSaved(Number(result?.proposedRate ?? selectedRate));
    } catch (e: any) {
      let message = 'Failed to update proposed rate. Please try again.';
      try {
        if (e?.message) {
          const parsed = typeof e.message === 'string' && e.message.startsWith('{')
            ? JSON.parse(e.message)
            : null;
          message = parsed?.error?.message || parsed?.message || e.message || message;
        }
      } catch {
        if (typeof e?.message === 'string' && e.message.length < 200) {
          message = e.message;
        }
      }
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [selectedRate, initialRate, job.id, onClose, onSaved]);

  return (
    <div
      id="job-posted-edit-proposed-rate-modal"
      data-testid="job-posted-edit-proposed-rate-modal"
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: 20,
        maxWidth: 420,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <h3 style={{ margin: '0 0 12px', color: '#0B4598', fontSize: '1.15rem' }}>
        Edit Proposed Rate
      </h3>
      <div className={postJobStyles.inputContainer}>
        <MaterialSelector
          id="job-posted-proposed-rate-select"
          name="proposedRate"
          label="Proposed Rate"
          value={selectedRate}
          onChange={(e: any) => setSelectedRate(Number(e.target.value))}
        >
          {rateOptions(Number(job.type) as 0 | 1 | 2, selectedRate)}
        </MaterialSelector>
        {showGuidance ? (
          <div className={postJobStyles.rateBenchmarkBox}>
            {rateGuidanceState === 'low' ? (
              <ReportProblemOutlinedIcon className={postJobStyles.rateBenchmarkIcon} />
            ) : rateGuidanceState === 'strong' ? (
              <FlashOnIcon className={postJobStyles.rateBenchmarkIcon} />
            ) : (
              <TrendingUpIcon className={postJobStyles.rateBenchmarkIcon} />
            )}
            <div>
              <div className={postJobStyles.rateBenchmarkText}>
                <strong>{rateGuidanceCopy!.title}</strong>
              </div>
              <div className={postJobStyles.rateBenchmarkHint}>
                {rateGuidanceCopy!.description}
              </div>
              {rateGuidanceCopy!.historicalContext && (
                <div className={postJobStyles.rateBenchmarkHint}>
                  {rateGuidanceCopy!.historicalContext}
                </div>
              )}
              {recommendedRate !== undefined && (
                <div className={postJobStyles.rateBenchmarkHint}>
                  Recommended proposed rate: <strong>${recommendedRate}/hr</strong>
                </div>
              )}
              {recommendedRate !== undefined
                && rateBenchmark?.typicalBookedRate
                && recommendedRate < rateBenchmark.typicalBookedRate && (
                <div className={postJobStyles.rateBenchmarkNote}>
                  <strong>Note:</strong> Talent may offer above your proposed rate.
                  Similar shifts with a Proposed Rate of ${recommendedRate}/hr
                  typically booked around ${rateBenchmark.typicalBookedRate}/hr.
                </div>
              )}
              {rateGuidanceCopy!.hint && (
                <div className={postJobStyles.rateBenchmarkHint}>
                  {rateGuidanceCopy!.hint}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
      {error ? (
        <div
          data-testid="job-posted-proposed-rate-error"
          style={{ color: '#FB7672', marginTop: 8, fontSize: '0.9rem' }}
        >
          {error}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 12, marginTop: 16, justifyContent: 'flex-end' }}>
        <span id="job-posted-proposed-rate-cancel-button" data-testid="job-posted-proposed-rate-cancel-button">
          <DefaultButton
            variant="outlined"
            color="primary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </DefaultButton>
        </span>
        <span id="job-posted-proposed-rate-save-button" data-testid="job-posted-proposed-rate-save-button">
          <DefaultButton
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || !selectedRate}
          >
            {saving ? 'Saving…' : 'Save'}
          </DefaultButton>
        </span>
      </div>
    </div>
  );
};

export default EditProposedRateModal;
